import React, { useState } from 'react';
import { Button, Pagination, SpaceBetween, Spinner, Table, TextFilter } from '@amzn/awsui-components-react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import Header from '@amzn/awsui-components-react/polaris/header';
import { ColumnDefinitions, PaginationLabels, TableEmptyState, TableNoMatchState } from './table-config';
import { useBundle } from '@amzn/react-arb-tools';
import { useQuery } from '@tanstack/react-query'
import { IQueryAccessLevelsParams, queryAccessLevels } from './utils';
import { debug } from '../../../utils/commonUtils';

const PAGE_SIZE = 10;

export interface RefreshCallBackInterface {
  (pageNumber: number): void;
}

export interface AccessLevelsTablePanelPropsInterface {
  employeeId: number;
}

export default function AccessLevelsTablePanel(props: AccessLevelsTablePanelPropsInterface ) {
  debug(`AccessLevelsTablePanel() props is ${JSON.stringify(props)}`);

  const queryAccessLevelsParams: IQueryAccessLevelsParams = {
    employeeId: props.employeeId.toString(),
    Orderby: 'accesslevelname',
    OrderbyDirection: 'a',
    PageNumber: 0,
    RecordsByPage: 500
  };

  const accessLevelsQuery = useQuery({
    queryKey: ['accessLevels'],
    queryFn: () => queryAccessLevels(queryAccessLevelsParams),
    retry: 3
  });

  const [bundle, isBundleLoading] = useBundle('components.SelfService.AccessLevels.TablePanel');

  const pageSize = PAGE_SIZE;

  const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(
    accessLevelsQuery?.data || [],
    {
      filtering: {
        empty: <TableEmptyState title={!isBundleLoading ? bundle.getMessage('no-access-levels-found') : 'No Assets Found'} />,
        noMatch: <TableNoMatchState onClearFilter={() => actions.setFiltering('')} />
      },
      pagination: { pageSize: pageSize },
      sorting: {},
    }
  );

  const getFilterCounterText = (count: number) => `${count} ${count === 1 ? 'match' : 'matches'}`;

  const [hideTable, setHideTable] = useState<boolean>(false);

  const itemsCount = (): number => {
    if (accessLevelsQuery.data?.length) return accessLevelsQuery.data.length;
    return 0;
  };

  const [refreshing, setRefreshing] = useState<boolean>(false);

  if (isBundleLoading) return <Spinner/>;

  return(
    <>
    <div id='tableDiv' hidden={hideTable}>
      <Table
        {...collectionProps}
        columnDefinitions={ColumnDefinitions}
        filter={
          <TextFilter
            {...filterProps}
            filteringAriaLabel='Filter Access Levels'
            filteringPlaceholder='Find Access Levels'
            countText={getFilterCounterText(filteredItemsCount === undefined ? 0: filteredItemsCount)}
          />
        }
        header={
          <Header
            counter={`(${itemsCount().toString()})`}
            actions={
              <>
              <SpaceBetween size='xs' direction='horizontal'>
                <Button
                  loading={refreshing}
                  key="refreshBtn"
                  onClick={async () => {
                    setRefreshing(true); 
                    await accessLevelsQuery.refetch()
                    setRefreshing(false); 
                  }}
                  iconName='refresh'>{bundle.getMessage('refresh')}
                </Button>
              </SpaceBetween>
              </>
            }
          >
            {bundle.getMessage('my-access-levels')}
          </Header>
        }
        footer={<></>}
        items={items}
        loading={accessLevelsQuery.isLoading}
        loadingText={bundle.getMessage('loading-access-levels')}
        pagination={
          accessLevelsQuery && accessLevelsQuery.data && accessLevelsQuery.data.length > pageSize
          &&
          <Pagination
            {...paginationProps}
            ariaLabels={PaginationLabels}
          />
        }
        resizableColumns={true}
        stickyHeader={true}
      />
    </div>
    </>
  );
}
